import {
  CheckCircleFilled,
  CloseCircleFilled,
  DownOutlined,
  FolderAddOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  RightOutlined,
} from '@ant-design/icons'
import {
  Collapse,
  Empty,
  Input,
  InputRef,
  message,
  Modal,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { FileUploader, useDocumentDownload } from 'features/files'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import { Download, Trash2 } from 'lucide-react'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryType } from 'types'

import { formatCategory } from './helpers/categoryUtils'

const ROLODEX_DOC_FIELDS_TYPE = RolodexEntryType.DOCUMENT_FIELDS

const { Panel } = Collapse
const { Text } = Typography

export interface DocumentNavigationSidebarProps {
  unitId: string
  siteId?: string
  organizationId?: string
  onUploadComplete?: (documentIds: ReadonlyArray<string>) => void
  showUploader?: boolean
  readOnly?: boolean
  categories: ReadonlyArray<string>
  requiredCategories?: ReadonlyArray<string>
  localCategories?: ReadonlyArray<string>
  metricsByCategory: ReadonlyMap<string, number>
  documentsByCategory: ReadonlyMap<
    string,
    ReadonlyArray<{
      type?: RolodexEntryType
      entry_id?: string
      filename?: string
      visible_name?: string
    }>
  >
  isLoading: boolean
  onRefresh: () => Promise<void>
  selectedCategory?: string
  onCategorySelect: (category: string | undefined) => void
  onAddLocalCategory?: (category: string) => void
  onRemoveLocalCategory?: (category: string) => void
}

const PASS_ICON_COLOR = '#237804'
const FAIL_ICON_COLOR = '#a8071a'

export const DocumentNavigationSidebar: FC<DocumentNavigationSidebarProps> = ({
  unitId,
  siteId,
  organizationId,
  onUploadComplete,
  showUploader = false,
  readOnly = false,
  categories,
  requiredCategories = [],
  localCategories = [],
  metricsByCategory,
  documentsByCategory,
  isLoading,
  onRefresh,
  onAddLocalCategory,
  onRemoveLocalCategory,
}) => {
  const { selectedDocumentId, setSelectedDocumentId } = useLibraDocument()
  const { downloadDocument, downloadingIds } = useDocumentDownload()
  const { rolodexPatch, rolodexDelete } = useRolodexEdit()
  const { authorizedAxios: axios } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()
  const [isDeleting, setIsDeleting] = useState(false)
  const [deletingDocument, setDeletingDocument] = useState<{
    id: string
    name: string
    type: RolodexEntryType
  } | null>(null)

  const allCategoriesSorted = useMemo(() => {
    return [...categories].toSorted((a, b) => {
      const aLabel = formatCategory(a)
      const bLabel = formatCategory(b)
      return aLabel.localeCompare(bLabel)
    })
  }, [categories])

  const defaultExpandedKeys = useMemo(() => {
    const requiredCats = allCategoriesSorted.filter((cat) =>
      requiredCategories.includes(cat)
    )
    return requiredCats.length > 0
      ? requiredCats
      : allCategoriesSorted.slice(0, 3)
  }, [allCategoriesSorted, requiredCategories])

  const [expandedKeys, setExpandedKeys] =
    useState<string[]>(defaultExpandedKeys)
  const [draggedDocument, setDraggedDocument] = useState<{
    documentId: string
    currentCategory: string
  } | null>(null)
  const [dropTargetCategory, setDropTargetCategory] = useState<string | null>(
    null
  )

  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false)
  const [newCategoryInput, setNewCategoryInput] = useState('')
  const newCategoryInputRef = useRef<InputRef | null>(null)

  const unitEntry = useMemo(() => {
    return {
      id: unitId,
      type: RolodexEntryType.UNIT,
    } as GeckoEntry
  }, [unitId])

  const handleDragStart = (
    documentId: string,
    currentCategory: string,
    e: React.DragEvent
  ) => {
    setDraggedDocument({ documentId, currentCategory })
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragEnd = () => {
    setDraggedDocument(null)
    setDropTargetCategory(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (category: string) => {
    if (draggedDocument && draggedDocument.currentCategory !== category) {
      setDropTargetCategory(category)
    }
  }

  const handleDragLeave = () => {
    setDropTargetCategory(null)
  }

  const handleDrop = async (targetCategory: string, e: React.DragEvent) => {
    e.preventDefault()
    setDropTargetCategory(null)

    if (!draggedDocument) return
    if (draggedDocument.currentCategory === targetCategory) {
      setDraggedDocument(null)
      return
    }

    try {
      await rolodexPatch([
        {
          id: draggedDocument.documentId,
          type: RolodexEntryType.REPORT,
          data: {
            categories: [targetCategory],
          },
        },
      ])

      message.success(`Moved document to ${formatCategory(targetCategory)}`)
      // If this was a local/temp category, it now has a real document — remove it
      // from local state so it doesn't duplicate after the backend refresh
      if (localCategories.includes(targetCategory)) {
        onRemoveLocalCategory?.(targetCategory)
      }
      await onRefresh()
    } catch (error) {
      console.error('Failed to update document category:', error)
      message.error('Failed to move document. Please try again.')
    } finally {
      setDraggedDocument(null)
    }
  }

  const handleDeleteDocument = useCallback(
    (
      documentId: string,
      documentName: string,
      documentType: RolodexEntryType
    ) => {
      setDeletingDocument({
        id: documentId,
        name: documentName,
        type: documentType,
      })
    },
    []
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingDocument) return

    setIsDeleting(true)
    try {
      const fieldsQueryResult = await axios.post(
        `${ROLODEX_URL}/api/v2/entries/load`,
        {
          type: ROLODEX_DOC_FIELDS_TYPE,
          tags: { [deletingDocument.type]: deletingDocument.id },
          limit: 1,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (
        fieldsQueryResult.data &&
        Array.isArray(fieldsQueryResult.data) &&
        fieldsQueryResult.data.length > 0
      ) {
        const fieldsEntryId = fieldsQueryResult.data[0].entry.id
        await rolodexDelete(fieldsEntryId, false)
      }

      await rolodexDelete(deletingDocument.id, true)

      message.success('Document deleted successfully')

      if (selectedDocumentId === deletingDocument.id) {
        setSelectedDocumentId(null)
      }

      await onRefresh()

      setDeletingDocument(null)
    } catch (error) {
      console.error('Failed to delete document:', error)
      message.error('Failed to delete document. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }, [
    deletingDocument,
    axios,
    ROLODEX_URL,
    rolodexDelete,
    selectedDocumentId,
    setSelectedDocumentId,
    onRefresh,
  ])

  const handleCancelDelete = useCallback(() => {
    if (!isDeleting) {
      setDeletingDocument(null)
    }
  }, [isDeleting])

  const handleDownloadDocument = useCallback(
    (
      event: React.MouseEvent,
      document: { entry_id?: string; filename?: string; visible_name?: string }
    ) => {
      event.stopPropagation()
      if (document.entry_id) {
        void downloadDocument({
          entry_id: document.entry_id,
          filename: document.filename,
          visible_name: document.visible_name,
        })
      }
    },
    [downloadDocument]
  )

  const renderDocumentList = (category: string) => {
    const count = metricsByCategory.get(category) ?? 0
    const label = formatCategory(category)
    const dropZoneHandlers = !readOnly
      ? {
          onDragOver: handleDragOver,
          onDragEnter: () => handleDragEnter(category),
          onDragLeave: handleDragLeave,
          onDrop: (e: React.DragEvent) => {
            void handleDrop(category, e)
          },
        }
      : {}

    if (count === 0) {
      return (
        <EmptyCategory {...dropZoneHandlers}>
          <Empty
            description={`No ${label.toLowerCase()} documents`}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ margin: '1rem 0' }}
          />
        </EmptyCategory>
      )
    }

    const documents = documentsByCategory.get(category) ?? []

    return (
      <DocumentList {...dropZoneHandlers}>
        {documents.map((document, index) => {
          const isSelected = selectedDocumentId === document.entry_id
          const displayName =
            document.filename ||
            document.visible_name ||
            `Document ${index + 1}`
          const isDragging = draggedDocument?.documentId === document.entry_id
          return (
            <DocumentItem
              key={document.entry_id || `${category}-doc-${index}`}
              $isSelected={isSelected}
              $isDragging={isDragging}
              $isDraggable={!readOnly}
              draggable={!readOnly}
              onDragStart={
                readOnly
                  ? undefined
                  : (e) => {
                      if (document.entry_id) {
                        handleDragStart(document.entry_id, category, e)
                      }
                    }
              }
              onDragEnd={readOnly ? undefined : handleDragEnd}
              onClick={() => {
                if (document.entry_id) {
                  setSelectedDocumentId(document.entry_id)
                }
              }}>
              <DocumentLabelWrapper>
                <Tooltip title={displayName} mouseEnterDelay={0.5}>
                  <DocumentLabel>{displayName}</DocumentLabel>
                </Tooltip>
              </DocumentLabelWrapper>
              <DownloadButton
                onClick={(e) => handleDownloadDocument(e, document)}
                disabled={
                  document.entry_id
                    ? downloadingIds.has(document.entry_id)
                    : false
                }
                title='Download document'>
                <Download size={14} />
              </DownloadButton>
              <DeleteButton
                onClick={(e) => {
                  e.stopPropagation()
                  if (document.entry_id && document.type) {
                    handleDeleteDocument(
                      document.entry_id,
                      displayName,
                      document.type
                    )
                  }
                }}
                disabled={isDeleting}
                title='Delete document'>
                <Trash2 size={14} />
              </DeleteButton>
            </DocumentItem>
          )
        })}
      </DocumentList>
    )
  }

  const handleAddCategory = useCallback(() => {
    if (readOnly) {
      return
    }
    const raw = newCategoryInput.trim()
    if (!raw) {
      setIsAddCategoryModalOpen(false)
      setNewCategoryInput('')
      return
    }

    const normalized = raw
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')

    if (!normalized) {
      message.warning('Category name contains no valid characters.')
      return
    }

    if (categories.includes(normalized)) {
      message.warning(
        `Category "${formatCategory(normalized)}" already exists.`
      )
      return
    }

    onAddLocalCategory?.(normalized)
    setExpandedKeys((prev) => [...prev, normalized])
    setIsAddCategoryModalOpen(false)
    setNewCategoryInput('')
    message.success(`Category "${formatCategory(normalized)}" created`)
  }, [readOnly, newCategoryInput, categories, onAddLocalCategory])

  const handleCancelAddCategory = useCallback(() => {
    setIsAddCategoryModalOpen(false)
    setNewCategoryInput('')
  }, [])

  const renderFolderHeader = (
    category: string,
    count: number,
    isExpanded: boolean,
    isLocal: boolean
  ) => {
    const isRequired = requiredCategories.includes(category)
    const label = formatCategory(category)
    const hasDocuments = count > 0

    return (
      <FolderHeader>
        <FolderLeft>
          {isExpanded ? (
            <FolderOpenOutlined style={{ fontSize: 14, color: '#1890ff' }} />
          ) : (
            <FolderOutlined style={{ fontSize: 14, color: '#8c8c8c' }} />
          )}
          <FolderTitle>{label}</FolderTitle>
          {isRequired && (
            <>
              {hasDocuments ? (
                <CheckCircleFilled
                  style={{
                    color: PASS_ICON_COLOR,
                    fontSize: 12,
                    marginLeft: 4,
                  }}
                />
              ) : (
                <CloseCircleFilled
                  style={{
                    color: FAIL_ICON_COLOR,
                    fontSize: 12,
                    marginLeft: 4,
                  }}
                />
              )}
            </>
          )}
          {isLocal && (
            <Tag
              color='orange'
              style={{ fontSize: 10, marginLeft: 4, padding: '0 4px' }}>
              temp
            </Tag>
          )}
        </FolderLeft>
        <Tag color={isRequired ? (hasDocuments ? 'green' : 'red') : 'blue'}>
          {count}
        </Tag>
      </FolderHeader>
    )
  }

  if (isLoading) {
    return (
      <SidebarContainer>
        <LoadingState>Loading documents...</LoadingState>
      </SidebarContainer>
    )
  }

  return (
    <SidebarContainer>
      <SidebarHeader>
        <HeaderContent>
          <Text strong>Document Navigation</Text>
          {!readOnly && (
            <AddCategoryIconButton
              onClick={() => setIsAddCategoryModalOpen(true)}
              title='Add new category'>
              <FolderAddOutlined />
            </AddCategoryIconButton>
          )}
        </HeaderContent>
      </SidebarHeader>

      <FoldersContainer>
        <StyledCollapse
          activeKey={expandedKeys}
          onChange={(keys) => setExpandedKeys(keys as string[])}
          ghost
          expandIcon={({ isActive }) =>
            isActive ? <DownOutlined /> : <RightOutlined />
          }>
          {allCategoriesSorted.map((category: string) => {
            const count = metricsByCategory.get(category) ?? 0
            const isExpanded = expandedKeys.includes(category)
            const isRequired = requiredCategories.includes(category)
            const isDropTarget = dropTargetCategory === category
            const isLocal = localCategories.includes(category)
            const headerDropZoneHandlers = !readOnly
              ? {
                  onDragOver: handleDragOver,
                  onDragEnter: () => handleDragEnter(category),
                  onDragLeave: handleDragLeave,
                  onDrop: (e: React.DragEvent) => {
                    void handleDrop(category, e)
                  },
                }
              : {}

            return (
              <Panel
                key={category}
                header={
                  <div {...headerDropZoneHandlers}>
                    {renderFolderHeader(category, count, isExpanded, isLocal)}
                  </div>
                }
                data-required={isRequired}
                data-drop-target={isDropTarget}
                data-local={isLocal}>
                {renderDocumentList(category)}
              </Panel>
            )
          })}
        </StyledCollapse>
      </FoldersContainer>

      {showUploader && (
        <UploadSection>
          <UploadContent>
            <FileUploader
              condensed
              showExistingFiles={false}
              entry={unitEntry}
              tags={{
                unit: unitId,
                ...(siteId ? { site: siteId } : {}),
                ...(organizationId ? { organization: organizationId } : {}),
              }}
              onUploadComplete={(documentIds) => {
                onRefresh()
                onUploadComplete?.(documentIds)
              }}
            />
          </UploadContent>
        </UploadSection>
      )}

      {!readOnly && (
        <Modal
          open={isAddCategoryModalOpen}
          title='Add New Category'
          okText='Create'
          onCancel={handleCancelAddCategory}
          onOk={handleAddCategory}>
          <Input
            ref={newCategoryInputRef}
            autoFocus
            placeholder='Enter category name (e.g., "Project Documents")'
            prefix={<FolderOutlined style={{ color: '#8c8c8c' }} />}
            value={newCategoryInput}
            onChange={(e) => setNewCategoryInput(e.target.value)}
            onPressEnter={handleAddCategory}
          />
          <Text type='secondary' style={{ fontSize: '0.75rem', marginTop: 8 }}>
            Category names will be normalized (lowercase, underscores for
            spaces)
          </Text>
        </Modal>
      )}

      <Modal
        open={Boolean(deletingDocument)}
        title='Delete Document?'
        okText='Delete'
        okButtonProps={{ danger: true, loading: isDeleting }}
        cancelButtonProps={{ disabled: isDeleting }}
        onCancel={handleCancelDelete}
        onOk={handleConfirmDelete}>
        Are you sure you want to delete{' '}
        <strong>{deletingDocument?.name}</strong>? This will permanently delete
        the document and all associated data.
      </Modal>
    </SidebarContainer>
  )
}

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.forecast.background1};
  border-right: 1px solid ${({ theme }) => theme.colors.grayscale[500]};
`

const SidebarHeader = styled.div`
  flex-shrink: 0;
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale[500]};
  background: ${({ theme }) => theme.forecast.background2};
`

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const AddCategoryIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.secondaryText};
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 16px;

  &:hover {
    background: rgba(24, 144, 255, 0.1);
    color: ${({ theme }) => theme.forecast.primary || '#1890ff'};
  }
`

const FoldersContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

const StyledCollapse = styled(Collapse)`
  background: transparent;
  border: none;

  .ant-collapse-item {
    border: none;
    background: transparent;
  }

  .ant-collapse-header {
    padding: 0.75rem 1rem !important;
    background: ${({ theme }) => theme.forecast.background2};
    border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale[500]};
    align-items: center;
  }

  .ant-collapse-content {
    border: none;
    background: transparent;
  }

  .ant-collapse-content-box {
    padding: 0;
  }

  .ant-collapse-item[data-required='true'] .ant-collapse-header {
    border-left: 3px solid #f59e0b;
  }

  .ant-collapse-item[data-required='true'] .ant-collapse-header:hover {
    background: rgba(245, 158, 11, 0.05) !important;
  }

  .ant-collapse-item[data-drop-target='true'] .ant-collapse-header {
    background: rgba(24, 144, 255, 0.1) !important;
    border-left-color: ${({ theme }) => theme.forecast.primary};
  }

  .ant-collapse-item[data-local='true'] .ant-collapse-header {
    opacity: 0.85;
    font-style: italic;
  }
`

const FolderHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const FolderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const FolderTitle = styled.span`
  font-weight: 600;
  font-size: 0.875rem;
`

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
`

const DocumentItem = styled.div<{
  $isSelected: boolean
  $isDragging?: boolean
  $isDraggable?: boolean
}>`
  padding: 0.5rem 0.75rem 0.5rem 2.5rem;
  cursor: ${({ $isDragging, $isDraggable }) =>
    !$isDraggable ? 'pointer' : $isDragging ? 'grabbing' : 'grab'};
  user-select: none;
  border-left: 2px solid transparent;
  transition: all 0.2s ease;
  background: rgba(0, 0, 0, 0.02);
  display: flex;
  align-items: center;
  min-width: 0;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};

  ${({ $isSelected }) =>
    $isSelected
      ? `
    background: rgba(24, 144, 255, 0.15) !important;
    border-left-color: rgba(24, 144, 255, 0.8) !important;
  `
      : ''}

  &:hover {
    background: rgba(24, 144, 255, 0.08) !important;
    border-left-color: rgba(24, 144, 255, 0.5) !important;
  }
`

const DocumentLabelWrapper = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
`

const DocumentLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  width: 100%;
`

const DocumentActionButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.25rem;
  margin-left: 0.5rem;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.secondaryText};
  border-radius: 4px;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${DocumentItem}:hover & {
    display: flex;
  }
`

const DownloadButton = styled(DocumentActionButton)`
  &:hover {
    background: rgba(24, 144, 255, 0.1);
    color: ${({ theme }) => theme.forecast.primary};
  }
`

const DeleteButton = styled(DocumentActionButton)`
  &:hover {
    background: rgba(255, 77, 79, 0.1);
    color: #ff4d4f;
  }
`

const EmptyCategory = styled.div`
  padding: 0.5rem 1rem;
`

const LoadingState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const UploadSection = styled.div`
  flex: 0 1 auto;
  min-height: 0;
  max-height: 50%;
  overflow-y: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale[500]};
  background: ${({ theme }) => theme.forecast.background2};
`

const UploadContent = styled.div`
  padding: 1rem;
`
